import { component } from "picoapp";
import { stringToHTML } from "../../utilities";

const scrollIntoViewWithOffset = (node: Element, offset: number) => {
  window.scrollTo({
    behavior: "smooth",
    top:
      node.getBoundingClientRect().top -
      document.body.getBoundingClientRect().top -
      offset,
  });
};

const parseDom = (stringToMarkup: string) => {
  const dom = stringToHTML(stringToMarkup);
  const grid = dom.querySelector("[data-tbsp-component='tbspCollectionGrid']");
  const items = grid.querySelectorAll("* > main > ol > li");
  const pageNextUrl = grid.querySelector<HTMLDivElement>("[data-page-next-url]")
    ?.dataset?.pageNextUrl;
  const pageNextSize = parseFloat(
    grid.querySelector<HTMLDivElement>("[data-page-next-size]")?.dataset
      ?.pageNextSize ?? "0"
  );

  const paginationCounter: HTMLParagraphElement = grid.querySelector(
    "#load-more-pagination p"
  );

  const paginationPercentage: HTMLDivElement = grid.querySelector(
    "#pagination-percentage"
  );

  return {
    dom,
    grid,
    items,
    pageNextUrl,
    pageNextSize,
    paginationCounter,
    paginationPercentage,
  };
};

const parseNextUrl = (nextPath: string, collectionId: string) => {
  const nextUrl = new URL(`${window.location.origin}${nextPath}`);
  nextUrl.searchParams.append("sections", collectionId);

  return nextUrl;
};

const createPlaceholders = (pageSize: number) => {
  return Array.from({ length: pageSize }, () => {
    const template = document.createElement("template");

    template.innerHTML = `
      <li class="animate-fade-in">
        <div class="animate-pulse box-border w-full h-auto pb-full rounded bg-bone-100"></div>
        <div class="box-border mt-2 mb-1 md:mt-4">
          <div class="animate-pulse rounded box-border w-1/2 h-5 mb-2 bg-placeholder"></div>
          <div class="animate-pulse rounded box-border w-4/5 h-3 mb-1 bg-placeholder hidden md:block"></div>
          <div class="animate-pulse rounded box-border w-1/2 h-3 mb-3 bg-placeholder hidden md:block"></div>
          <div class="animate-pulse rounded box-border w-3/4 md:w-1/2 h-3 md:h-4 bg-placeholder"></div>
          <div class="box-border w-full h-3 md:h-4"></div>
          <div class="animate-pulse rounded box-border w-full h-5 md:h-7 bg-placeholder"></div>
        </div>
      </li>
    `;

    return template.content.firstElementChild;
  });
};

export const collectionPagination = component(
  (node: HTMLButtonElement, ctx) => {
    const pageOnScroll = parseFloat(node.dataset.pageOnscroll ?? "0");
    let currentPageOnScroll = 0;
    const collectionGrid = node.closest(
      "[data-tbsp-component='tbspCollectionGrid']"
    );
    const collectionSection = collectionGrid.closest("[id^='shopify-section']");
    const pagination: HTMLDivElement = collectionGrid.querySelector(
      "#load-more-pagination"
    );

    if (!pagination) {
      return;
    }

    pagination.classList.replace("hidden", "flex");

    const loadMoreButton = pagination.querySelector("button");
    const paginationPercentage: HTMLDivElement = pagination.querySelector(
      "#pagination-percentage"
    );
    const [, collectionId] = collectionSection
      .getAttribute("id")
      .split("shopify-section-");

    if (!loadMoreButton) {
      return;
    }

    const loadMoreHandler = async (e: Event) => {
      e.preventDefault();

      loadMoreButton.setAttribute("disabled", "true");
      const pageNextSize = parseFloat(node.dataset.pageNextSize ?? "0");
      const pageNextUrl = node.dataset.pageNextUrl;
      const placeholders = createPlaceholders(pageNextSize);
      collectionGrid.querySelector("* > main > ol").append(...placeholders);

      if (!pageNextUrl) {
        return;
      }

      let partialMarkup: string;

      try {
        const result = await fetch(
          parseNextUrl(pageNextUrl, collectionId)
        ).then((res) => res.json());
        partialMarkup = result[collectionId];
      } catch (e) {
        console.error(e);
      }

      if (!partialMarkup) {
        return;
      }

      const xDom = parseDom(partialMarkup);

      placeholders.forEach((ph, idx) => {
        if (!xDom.items[idx]) {
          ph.parentElement.removeChild(ph);
        } else {
          ph.replaceWith(xDom.items[idx]);
        }
      });

      node.setAttribute("data-page-next-url", xDom.pageNextUrl);
      node.setAttribute("data-page-next-size", String(xDom.pageNextSize));

      window.document.dispatchEvent(
        new Event("shopify:section:load", {
          bubbles: true,
          cancelable: true,
        })
      );

      setTimeout(() => {
        scrollIntoViewWithOffset(xDom.items[0], 100);
      }, 250);

      const paginationCounter: HTMLParagraphElement =
        pagination.querySelector("p");

      if (currentPageOnScroll === pageOnScroll) {
        node.classList.replace("opacity-0", "opacity-100");
        node.classList.replace("translate-y-full", "translate-y-0");
      }

      if (!xDom.paginationCounter) {
        paginationCounter.textContent = `${pagination.dataset.totalItems} / ${pagination.dataset.totalItems}`;
        paginationPercentage.style.width = "100%";

        setTimeout(() => {
          node.classList.replace("opacity-100", "opacity-0");
          node.classList.replace("translate-y-0", "translate-y-full");
        }, 500);
      } else {
        paginationCounter.replaceWith(xDom.paginationCounter);
        paginationPercentage.style.width =
          xDom.paginationPercentage.style.width;
        loadMoreButton.removeAttribute("disabled");
      }
    };

    if (pageOnScroll > 0) {
      node.classList.add(...["opacity-0", "translate-y-full"]);
    }

    loadMoreButton.addEventListener("click", loadMoreHandler);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            currentPageOnScroll = currentPageOnScroll + 1;

            // Fire a syntetic load more event
            loadMoreButton.click();

            if (currentPageOnScroll === pageOnScroll) {
              observer.unobserve(node);
            }
          }
        });
      },
      { rootMargin: "0px" }
    );

    if (pageOnScroll > 0) {
      observer.observe(node);
    }

    return (node: HTMLButtonElement) => {
      node.removeEventListener("click", loadMoreHandler);
    };
  }
);
